<script lang="ts">
import { computed, defineComponent, ref, shallowRef } from 'vue'

export default defineComponent({
  name: 'LazyImage',
  props: {
    alt: { type: String, default: undefined },
    contain: { type: Boolean },
    cover: { type: Boolean },
    eager: { type: Boolean },
    height: { type: Number, default: undefined },
    inline: { type: Boolean },
    imageProps: { type: Object, default: undefined },
    lazySrc: { type: String, default: undefined },
    presentation: { type: Boolean },
    sizeBy: { type: String, validator: (value: string) => ['width', 'height'].includes(value), default: 'width' },
    src: { type: String, required: true },
    width: { type: Number, default: undefined }
  },
  emits: ['load'],
  setup (props, { expose }) {
    const loaded = ref(false)
    const img = shallowRef<HTMLImageElement>()
    const lazyImg = shallowRef<HTMLImageElement>()

    expose({
      img,
      lazyImg,
      loaded
    })

    return {
      img,
      lazyImg,
      loaded,
      imageAttrs: computed(() => ({
        ...props.imageProps,
        ...props.presentation ? { role: 'presentation' } : null
      }))
    }
  }
})
</script>

<template>
  <div class="vue-boost-lazyimage">
    <Transition>
      <img
        v-if="lazySrc && !loaded"
        ref="lazyImg"
        :loading="eager ? 'eager' : 'lazy'"
        decoding="async"
        draggable="false"
        role="presentation"
        class="vue-boost-lazyimage-fallback"
        :class="[sizeBy, { cover, contain, 'inline-block': inline }]"
        :src="lazySrc"
        :width="width"
        :height="height"
      />
    </Transition>
    <img
      ref="img"
      decoding="async"
      draggable="false"
      v-bind="imageAttrs"
      :loading="eager ? 'eager' : 'lazy'"
      class="vue-boost-lazyimage-img"
      :class="[sizeBy, { cover, contain, 'inline-block': inline , loaded }]"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      @load="event => {
        loaded = true
        $emit('load', event)
      }"
    />
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.vue-boost-lazyimage {
  position: relative;
  width: 100%;
  height: 100%;

  &-img,
  &-fallback {
    position: relative;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s;
    -moz-user-drag: none;
    user-select: none;

    &.width {
      width: 100%;
      height: auto;
    }

    &.height {
      width: auto;
      height: 100%;
    }

    &.cover,
    &.contain {
      width: 100%;
      height: 100%;
    }

    &.cover {
      object-fit: cover;
    }

    &.contain {
      object-fit: contain;
    }

    &.inline-block {
      display: inline-block;
    }
  }

  &-img {
    &.loaded {
      opacity: 1;
    }
  }

  &-fallback {
    position: absolute;
    z-index: 0;
    opacity: 1;
  }
}
</style>
